import { useState, useEffect, useMemo, useCallback } from "react";

import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { getContentClient } from "sj-ui-toolkit/src/client";

import S from "./SearchInsights.module.scss";

const TIME_RANGE_OPTIONS = [
  { label: "Past week", value: "week" },
  { label: "Past month", value: "month" },
  { label: "Past year", value: "year" },
];

const SCOPE_OPTIONS = [
  { label: "My tenant", value: "tenant" },
  { label: "Platform-wide", value: "platform" },
];

const getSinceDate = timeRange => {
  const since = new Date();
  switch (timeRange) {
    case "week":
      since.setDate(since.getDate() - 7);
      break;
    case "year":
      since.setFullYear(since.getFullYear() - 1);
      break;
    case "month":
    default:
      since.setMonth(since.getMonth() - 1);
      break;
  }
  return since;
};

const formatBucketLabel = (bucketStart, granularity) => {
  if (!bucketStart) {
    return "";
  }
  const date = new Date(bucketStart);
  if (granularity === "hourly") {
    return date.toLocaleTimeString(undefined, { hour: "numeric" });
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const getBucketGranularity = buckets => {
  if (buckets.length < 2) {
    return null;
  }
  const first = new Date(buckets[0].bucketStart).getTime();
  const second = new Date(buckets[1].bucketStart).getTime();
  const diffHours = Math.abs(second - first) / (1000 * 60 * 60);

  if (diffHours < 20) {
    return "hourly";
  }
  if (diffHours < 24 * 6) {
    return "daily";
  }
  return "weekly";
};

const SearchInsights = ({ visible, onHide, selectedTenant }) => {
  const [timeRange, setTimeRange] = useState("month");
  const [scope, setScope] = useState("tenant");
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const contentClient = getContentClient();

  const loadInsights = useCallback(async () => {
    if (!selectedTenant) {
      setInsights(null);
      setIsLoading(false);
      setError(new Error("No tenant selected"));
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const since = getSinceDate(timeRange);
      const data = await contentClient.fetchSearchInsights(selectedTenant, since);
      setInsights(data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [contentClient, selectedTenant, timeRange]);

  useEffect(() => {
    if (visible) {
      loadInsights();
    }
  }, [visible, loadInsights]);

  const searchVolumeBuckets = insights?.searchVolumeOverTime || [];
  const bucketGranularity = useMemo(
    () => getBucketGranularity(searchVolumeBuckets),
    [searchVolumeBuckets]
  );

  const chartData = useMemo(
    () =>
      searchVolumeBuckets.map(bucket => ({
        label: formatBucketLabel(bucket.bucketStart, bucketGranularity),
        count: Number(bucket.count) || 0,
      })),
    [searchVolumeBuckets, bucketGranularity]
  );

  const chartTitle = bucketGranularity ? `Search Volume (${bucketGranularity})` : "Search Volume";

  const topQueries =
    (scope === "platform" ? insights?.allTenantsTopQueries : insights?.tenantTopQueries) || [];
  const maxQueryCount = topQueries.reduce((max, q) => Math.max(max, Number(q.count) || 0), 0);
  const totalSearches = chartData.reduce((sum, bucket) => sum + bucket.count, 0);

  return (
    <Dialog
      header="Search Insights"
      visible={visible}
      onHide={onHide}
      style={{ width: "90vw", maxWidth: "1100px" }}
      className={S.dialog}
    >
      <div className={S.controls}>
        <Dropdown
          value={timeRange}
          options={TIME_RANGE_OPTIONS}
          onChange={e => setTimeRange(e.value)}
          className={S.rangeDropdown}
          aria-label="Select time range"
        />
        {!isLoading && !error && (
          <span className={S.totalSearches}>{totalSearches.toLocaleString()} searches</span>
        )}
        {isLoading && <Skeleton width="80px" height="0.875rem" className={S.totalSearches} />}
      </div>

      {!!error && !isLoading && (
        <div className={S.status}>Unable to load search insights right now.</div>
      )}

      {isLoading && (
        <div className={S.panels}>
          <SearchVolumeSkeleton />
          <TopQueriesSkeleton />
        </div>
      )}

      {!isLoading && !error && (
        <div className={S.panels}>
          <div className={S.panel}>
            <h3 className={S.panelTitle}>{chartTitle}</h3>
            <div className={S.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: "var(--text-color)", fillOpacity: 0.08 }}
                    contentStyle={{
                      background: "var(--surface-elevation-2)",
                      border: "1px solid var(--surface-300)",
                      borderRadius: 8,
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                    }}
                    labelStyle={{ color: "var(--text-color-secondary)" }}
                    itemStyle={{ color: "var(--primary-color)" }}
                  />
                  <Bar dataKey="count" fill="var(--primary-color)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={S.panel}>
            <div className={S.panelHeader}>
              <h3 className={S.panelTitle}>Top Searches</h3>
              <Dropdown
                value={scope}
                options={SCOPE_OPTIONS}
                onChange={e => setScope(e.value)}
                className={S.scopeDropdown}
                aria-label="Select query scope"
              />
            </div>
            <ol className={S.queryList}>
              {topQueries.map((query, index) => (
                <li key={`${query.query}-${index}`} className={S.queryItem}>
                  <span className={S.queryRank}>{index + 1}</span>
                  <div className={S.queryDetails}>
                    <div className={S.queryRow}>
                      <span className={S.queryText}>{query.query}</span>
                      <span className={S.queryCount}>{query.count}</span>
                    </div>
                    <div className={S.queryBarTrack}>
                      <div
                        className={S.queryBarFill}
                        style={{
                          width: maxQueryCount
                            ? `${(Number(query.count) / maxQueryCount) * 100}%`
                            : "0%",
                        }}
                      />
                    </div>
                  </div>
                </li>
              ))}
              {topQueries.length === 0 && (
                <li className={S.emptyState}>No search queries in this time range.</li>
              )}
            </ol>
          </div>
        </div>
      )}
    </Dialog>
  );
};

const CHART_SKELETON_BAR_HEIGHTS = [35, 55, 25, 90, 50, 65, 40, 58, 30, 48, 20, 52];
const TOP_QUERIES_SKELETON_ROW_COUNT = 7;

const SearchVolumeSkeleton = () => (
  <div className={S.panel}>
    <Skeleton width="10rem" height="1rem" className={S.skeletonTitle} />
    <div className={S.chartSkeleton}>
      {CHART_SKELETON_BAR_HEIGHTS.map((height, index) => (
        <Skeleton key={index} className={S.chartSkeletonBar} height={`${height}%`} />
      ))}
    </div>
  </div>
);

const TopQueriesSkeleton = () => (
  <div className={S.panel}>
    <div className={S.panelHeader}>
      <Skeleton width="7rem" height="1rem" />
      <Skeleton width="140px" height="2.25rem" borderRadius="6px" />
    </div>
    <ol className={S.queryList}>
      {Array.from({ length: TOP_QUERIES_SKELETON_ROW_COUNT }).map((_, index) => (
        <li key={index} className={S.queryItem}>
          <Skeleton width="1rem" height="0.875rem" />
          <div className={S.queryDetails}>
            <div className={S.queryRow}>
              <Skeleton width={`${70 - index * 6}%`} height="0.9375rem" />
              <Skeleton width="1.5rem" height="0.875rem" />
            </div>
            <Skeleton width="100%" height="3px" borderRadius="2px" />
          </div>
        </li>
      ))}
    </ol>
  </div>
);

export default SearchInsights;
